/**
 * What the landing page promises about framework coverage.
 *
 * **This is marketing copy with a compliance product's obligations.** Every
 * claim on this list is one a prospect will hold us to, and a framework listed
 * as covered when the catalogue has no mappings for it is not an exaggeration,
 * it is a misrepresentation — the same failure the review rules describe when
 * they forbid calling an automated review a peer review. So the status is part
 * of the data rather than something the layout implies, and there are exactly
 * two values:
 *
 *   * `covered` — the control catalogue maps to it today. Only a framework that
 *     appears in `packages/catalogue/src/frameworks/` may carry this.
 *   * `planned` — we intend to, and are saying so as an intention. A visitor
 *     reading this list must be able to tell the difference at a glance, which
 *     is why the two groups are rendered apart rather than as one list with a
 *     small badge.
 *
 * The order within each group is deliberate: what a buyer asks for first comes
 * first, not what is easiest to build.
 */

export type FrameworkStatus = 'covered' | 'planned';

export interface FrameworkEntry {
  readonly id: string;
  /** As the framework calls itself. Not abbreviated to fit a card. */
  readonly name: string;
  /** The issuing body or jurisdiction, so a reader can tell two similar names apart. */
  readonly authority: string;
  readonly status: FrameworkStatus;
  /** One line on what DriftWatch actually evidences for it. Never a claim of certification. */
  readonly evidences: string;
}

export const FRAMEWORKS: readonly FrameworkEntry[] = [
  {
    id: 'iso-27001',
    name: 'ISO/IEC 27001:2022',
    authority: 'ISO/IEC · Annex A',
    status: 'covered',
    evidences:
      'Access control, secure authentication, privileged access, configuration management, logging and data leakage prevention — the Annex A controls an auditor samples from your tenant.',
  },
  {
    id: 'soc-2',
    name: 'SOC 2',
    authority: 'AICPA · Trust Services Criteria',
    status: 'covered',
    evidences:
      'CC6 logical access and CC7 monitoring, evidenced per client with the date and the configuration that produced each verdict.',
  },
  {
    id: 'gdpr',
    name: 'UK & EU GDPR',
    authority: 'Article 32',
    status: 'covered',
    evidences:
      'Technical measures for security of processing, including where data is exposed outside the organisation.',
  },
  {
    id: 'cyber-essentials',
    name: 'Cyber Essentials & Plus',
    authority: 'IASME · UK',
    status: 'covered',
    evidences:
      'All five technical controls, on different terms: user access control and secure configuration from the tenant itself; firewalls, updates and malware protection only as far as Intune policy reaches managed devices, never across devices a tenant cannot see.',
  },
  {
    id: 'cis-v8',
    name: 'CIS Critical Security Controls v8',
    authority: 'Center for Internet Security',
    status: 'covered',
    evidences:
      'Account management, access control management, secure configuration, data protection, audit log management and email protection safeguards — for this tenant, not for the whole enterprise the safeguards are scoped to.',
  },
  {
    id: 'cis-m365',
    name: 'CIS Microsoft 365 Foundations Benchmark',
    authority: 'Center for Internet Security',
    status: 'covered',
    evidences:
      'Forty-three recommendations from v4.0.0, cited by number. The benchmark is written against the same tenant settings DriftWatch already reads.',
  },
  {
    id: 'nist-csf',
    name: 'NIST Cybersecurity Framework 2.0',
    authority: 'NIST · United States',
    status: 'covered',
    evidences:
      'Eleven Subcategories across Identify, Protect and Detect. Outcomes rather than controls, so this is evidence towards them and never a determination that one is met.',
  },
  {
    id: 'nis2',
    name: 'NIS2',
    authority: 'EU Directive 2022/2555',
    status: 'planned',
    evidences: 'Article 21 technical measures, for the sectors now in scope across the EU.',
  },
  {
    id: 'dora',
    name: 'DORA',
    authority: 'EU Regulation 2022/2554',
    status: 'planned',
    evidences: 'ICT risk management evidence for financial entities and their providers.',
  },
  {
    id: 'essential-eight',
    name: 'Essential Eight',
    authority: 'ACSC · Australia',
    status: 'planned',
    evidences: 'Maturity evidence for multi-factor authentication and restricting administrative privileges.',
  },
  {
    id: 'hipaa',
    name: 'HIPAA Security Rule',
    authority: 'HHS · United States',
    status: 'planned',
    evidences: '§164.312 technical safeguards, for practices running on Microsoft 365.',
  },
  {
    id: 'nhs-dspt',
    name: 'NHS Data Security & Protection Toolkit',
    authority: 'NHS England',
    status: 'planned',
    evidences: 'The access-control and device assertions the toolkit asks a supplier to make each year.',
  },
];

export const coveredFrameworks = FRAMEWORKS.filter((entry) => entry.status === 'covered');
export const plannedFrameworks = FRAMEWORKS.filter((entry) => entry.status === 'planned');
